import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  authorizeM365Connection,
  disconnectM365Connection,
  getM365Connection,
} from "@/api/client/m365-connection"
import { Button } from "@/components/ui/button"

import { M365_CALLBACK_PATH, M365_RETURN_KEY } from "../utils/m365-connection"

/** Shared background connection for flows and agents; no provider-specific duplicate state. */

export function M365ConnectionCard({ preserveDraft = false }: { preserveDraft?: boolean }) {
  const { t } = useTranslation()

  const client = useQueryClient()

  const [disconnected, setDisconnected] = useState(false)

  const query = useQuery({
    queryKey: ["m365-connection"],
    queryFn: getM365Connection,
    retry: false,
  })

  const connect = useMutation({
    mutationFn: async () => {
      const result = await authorizeM365Connection(`${window.location.origin}${M365_CALLBACK_PATH}`)

      const target = new URL(result.auth_url)

      if (target.protocol !== "https:") throw new Error("Invalid authorization URL")

      sessionStorage.setItem(
        M365_RETURN_KEY,
        `${location.pathname}${location.search}${location.hash}`
      )

      window.location.assign(target.href)
    },
  })

  const disconnect = useMutation({
    mutationFn: disconnectM365Connection,
    onSuccess: (data) => {
      client.setQueryData(["m365-connection"], data)

      setDisconnected(data.entra_revocation_required === true)
    },
  })

  const cleanupPending =
    query.isSuccess && query.data.status === "revoked" && query.data.cleanup_pending === true
  const canDisconnect =
    query.isSuccess &&
    (["connected", "reconnect_required", "insufficient_scopes", "error"].includes(
      query.data.status
    ) ||
      cleanupPending)
  const busy = connect.isPending || disconnect.isPending
  let connectionLabel = t("agents.connection.loading", { defaultValue: "Checking connection…" })
  if (query.isError && (query.error as { status?: number } | null)?.status === 503)
    // The M365 connection service itself is down on this deployment (#3207).
    connectionLabel = t("agents.connection.service_unavailable", {
      defaultValue: "Microsoft 365 is unavailable on this deployment right now.",
    })
  else if (query.isError)
    connectionLabel = t("agents.connection.unavailable", {
      defaultValue: "Connection status is unavailable.",
    })
  else if (query.data)
    connectionLabel = t(`agents.connection.status.${query.data.status}`, {
      defaultValue: query.data.status,
    })

  return (
    <section
      aria-label={t("agents.connection.title", { defaultValue: "Microsoft 365 connection" })}
      className="space-y-3 rounded-lg border p-4"
    >
      <h3 className="text-sm font-semibold">
        {t("agents.connection.title", { defaultValue: "Microsoft 365 connection" })}
      </h3>

      <p className="text-muted-foreground text-sm">
        {t("agents.connection.help", {
          defaultValue:
            "Your own connection is shared by flows and agents. Scheduled Microsoft 365 access requires explicit permission on each routine.",
        })}
      </p>

      <p className="text-sm" role="status">
        {connectionLabel}
      </p>

      {cleanupPending ? (
        <p className="text-muted-foreground text-sm" role="status">
          {t("agents.connection.cleanup_pending", {
            defaultValue:
              "Access is revoked, but stored credentials still need to be removed from this app.",
          })}
        </p>
      ) : null}

      {disconnected ? (
        <p className="text-muted-foreground text-sm" role="status">
          {t("agents.connection.disconnected", {
            defaultValue:
              "Stored credentials were removed from this app. Microsoft consent remains until you or an administrator revoke it in Microsoft Entra.",
          })}
        </p>
      ) : null}

      {connect.isError || disconnect.isError ? (
        <p className="text-sm" role="alert">
          {t("agents.connection.failed", {
            defaultValue:
              "The connection could not be updated. Try again or contact your administrator.",
          })}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {preserveDraft ? (
          <Button asChild variant="outline">
            <a href="/auth/m365/connection" rel="noopener noreferrer" target="_blank">
              {t("agents.connection.new_tab", { defaultValue: "Manage connection in a new tab" })}
            </a>
          </Button>
        ) : (
          <Button
            disabled={busy || query.isPending}
            type="button"
            variant="outline"
            onClick={() => connect.mutate()}
          >
            {t("agents.connection.connect", { defaultValue: "Connect / reconnect Microsoft 365" })}
          </Button>
        )}

        {canDisconnect ? (
          <Button
            disabled={busy}
            type="button"
            variant="outline"
            onClick={() => disconnect.mutate()}
          >
            {cleanupPending
              ? t("agents.connection.retry_cleanup", { defaultValue: "Retry credential cleanup" })
              : t("agents.connection.disconnect", { defaultValue: "Disconnect from this app" })}
          </Button>
        ) : null}

        {query.isError ? (
          <Button type="button" variant="ghost" onClick={() => void query.refetch()}>
            {t("agents.connection.retry", { defaultValue: "Refresh status" })}
          </Button>
        ) : null}
      </div>
    </section>
  )
}
